import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { HomeSeoBlocks } from './HomeSeoBlocks';
import { AppHeader } from './AppHeader';
import { SEO } from '../seo/SEO';
import { track } from '../lib/analytics';
import './LandingView.css';

interface Props {
  onTryAsGuest: () => void;
}

/* ── Mini Sudoku önizleme tahta ── */
function PreviewBoard() {
  // Hafif, tek bakışta "ürün" hissi veren mini bulmaca. 4 vurgulu rakam +
  // diagonal blok + 1 ipucu hücresi.
  const cells: Array<{ r: number; c: number; v: number; kind?: 'given' | 'hint' | 'note' }> = [
    { r: 0, c: 1, v: 5, kind: 'given' },
    { r: 0, c: 4, v: 3, kind: 'given' },
    { r: 0, c: 7, v: 9, kind: 'given' },
    { r: 1, c: 0, v: 6, kind: 'given' },
    { r: 1, c: 3, v: 7, kind: 'given' },
    { r: 2, c: 2, v: 8, kind: 'given' },
    { r: 2, c: 5, v: 1, kind: 'given' },
    { r: 2, c: 8, v: 2, kind: 'given' },
    { r: 3, c: 1, v: 9, kind: 'given' },
    { r: 3, c: 6, v: 5, kind: 'given' },
    { r: 4, c: 4, v: 6, kind: 'hint' },
    { r: 4, c: 8, v: 8, kind: 'given' },
    { r: 5, c: 2, v: 4, kind: 'given' },
    { r: 5, c: 7, v: 7, kind: 'given' },
    { r: 6, c: 0, v: 1, kind: 'given' },
    { r: 6, c: 3, v: 4, kind: 'given' },
    { r: 6, c: 6, v: 8, kind: 'given' },
    { r: 7, c: 5, v: 9, kind: 'given' },
    { r: 7, c: 8, v: 3, kind: 'given' },
    { r: 8, c: 1, v: 2, kind: 'given' },
    { r: 8, c: 4, v: 7, kind: 'given' },
  ];
  const size = 360;
  const cell = size / 9;
  return (
    <div className="landing-board" aria-hidden="true">
      <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%">
        <defs>
          <linearGradient id="lbHint" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6B8C68" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#6B8C68" stopOpacity="0.06" />
          </linearGradient>
        </defs>
        {/* hint cell highlight */}
        <rect x={4 * cell} y={4 * cell} width={cell} height={cell} fill="url(#lbHint)" />
        {/* row + col soft highlight (rule of thirds) */}
        <rect x={0} y={4 * cell} width={size} height={cell} fill="rgba(107,140,104,0.05)" />
        <rect x={4 * cell} y={0} width={cell} height={size} fill="rgba(107,140,104,0.05)" />
        {/* grid lines */}
        {Array.from({ length: 10 }).map((_, i) => {
          const isMajor = i % 3 === 0;
          const stroke = isMajor ? 'currentColor' : 'currentColor';
          const opacity = isMajor ? 0.55 : 0.18;
          const w = isMajor ? 1.6 : 1;
          return (
            <g key={i} opacity={opacity}>
              <line x1={i * cell} y1={0} x2={i * cell} y2={size} stroke={stroke} strokeWidth={w} />
              <line x1={0} y1={i * cell} x2={size} y2={i * cell} stroke={stroke} strokeWidth={w} />
            </g>
          );
        })}
        {/* numbers */}
        {cells.map((c, i) => {
          const cx = c.c * cell + cell / 2;
          const cy = c.r * cell + cell / 2 + cell * 0.07;
          const fill = c.kind === 'hint' ? '#6B8C68' : 'currentColor';
          const opacity = c.kind === 'hint' ? 1 : 0.78;
          const weight = c.kind === 'hint' ? 800 : 600;
          return (
            <text
              key={i}
              x={cx}
              y={cy}
              textAnchor="middle"
              fontSize={cell * 0.52}
              fontWeight={weight}
              fontFamily="'Inter','Segoe UI',system-ui,sans-serif"
              fill={fill}
              opacity={opacity}
            >
              {c.v}
            </text>
          );
        })}
      </svg>
      {/* Floating chips */}
      <div className="landing-chip landing-chip--rank" aria-hidden="true">
        <span className="landing-chip-dot landing-chip-dot--rank" />
        <span>1834 ELO</span>
      </div>
      <div className="landing-chip landing-chip--time" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        <span>04:12</span>
      </div>
    </div>
  );
}

/* ── Feature ikonları ─── */
function IconBrain() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4a3 3 0 0 0-3 3v0a3 3 0 0 0-2 5v0a3 3 0 0 0 2 5v0a3 3 0 0 0 3 3" />
      <path d="M15 4a3 3 0 0 1 3 3v0a3 3 0 0 1 2 5v0a3 3 0 0 1-2 5v0a3 3 0 0 1-3 3" />
      <path d="M12 4v16" />
    </svg>
  );
}
function IconTrophy() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
      <path d="M7 6H4v2a3 3 0 0 0 3 3" />
      <path d="M17 6h3v2a3 3 0 0 1-3 3" />
      <path d="M9 21h6" />
      <path d="M12 17v4" />
    </svg>
  );
}
function IconFlame() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3c2 4-2 6-2 9a4 4 0 1 0 8 0c0-3-3-3-3-7 0 0-3 1-3-2z" />
      <path d="M9 16a3 3 0 0 0 6 0" />
    </svg>
  );
}

export function LandingView({ onTryAsGuest }: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const tt = t.home as any;

  const goToAuth = (placement: string) => {
    track('cta_click', { placement, action: 'register' });
    navigate('/auth');
  };

  const continueAsGuest = (placement: string) => {
    track('cta_click', { placement, action: 'guest' });
    onTryAsGuest();
  };

  return (
    <div className="landing-page">
      <SEO path="/" />
      <AppHeader />

      {/* Ambient gradient backdrop */}
      <div className="landing-aurora" aria-hidden="true">
        <span className="landing-aurora-blob landing-aurora-blob--green" />
        <span className="landing-aurora-blob landing-aurora-blob--amber" />
        <span className="landing-aurora-blob landing-aurora-blob--blue" />
      </div>

      {/* HERO ─────────────────────────────────────────────── */}
      <section className="landing-hero">
        <div className="landing-hero-text">
          <span className="landing-eyebrow">
            <span className="landing-eyebrow-dot" />
            {tt.landingEyebrow}
          </span>
          <h1 className="landing-title">{tt.landingTitle}</h1>
          <p className="landing-sub">{tt.landingSub}</p>

          <div className="landing-ctas">
            <button
              type="button"
              className="landing-cta landing-cta--primary"
              onClick={() => goToAuth('landing_hero')}
            >
              {tt.landingRegisterCta}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M13 6l6 6-6 6" />
              </svg>
            </button>
            <button
              type="button"
              className="landing-cta landing-cta--ghost"
              onClick={() => continueAsGuest('landing_hero')}
            >
              {tt.landingGuestCta}
            </button>
          </div>

          <ul className="landing-features-inline" aria-hidden="true">
            <li><span className="landing-feat-dot" />{tt.landingFeature1}</li>
            <li><span className="landing-feat-dot" />{tt.landingFeature2}</li>
            <li><span className="landing-feat-dot" />{tt.landingFeature3}</li>
          </ul>
        </div>

        <div className="landing-hero-visual">
          <PreviewBoard />
        </div>
      </section>

      {/* TRUST STRIP ─────────────────────────────────────── */}
      <section className="landing-trust">
        <div className="landing-trust-item">
          <div className="landing-trust-stars" aria-hidden="true">
            {[0, 1, 2, 3, 4].map(i => (
              <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1z" />
              </svg>
            ))}
          </div>
          <div className="landing-trust-meta">
            <strong>{tt.landingTrustRating}</strong>
            <span>{tt.landingTrustRatingLabel}</span>
          </div>
        </div>
        <span className="landing-trust-sep" />
        <div className="landing-trust-item">
          <strong className="landing-trust-big">{tt.landingTrustPlayers}</strong>
          <span>{tt.landingTrustPlayersLabel}</span>
        </div>
        <span className="landing-trust-sep" />
        <div className="landing-trust-item">
          <strong className="landing-trust-big">{tt.landingTrustFree}</strong>
          <span>{tt.landingTrustFreeLabel}</span>
        </div>
      </section>

      {/* APP DOWNLOAD ──────────────────────────────────────── */}
      <section className="landing-app-download">
        <p className="landing-app-download-label">{t.home.mobileAppCta}</p>
        <div className="landing-app-download-row">
          <a
            href="https://apps.apple.com/tr/app/sudoku-area/id6768587148?l=tr"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-app-download-btn"
            aria-label={t.common.downloadAppStore}
            onClick={() => track('store_click', { store: 'ios', placement: 'landing_download' })}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.27.07 2.14.82 2.88.84.85 0 2.45-.98 4.12-.83 1.74.17 2.96.89 3.79 2.02-3.41 2.04-2.85 6.52.57 7.89-.45 1.08-.93 2.14-1.36 2.94zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <span>{t.common.downloadAppStore}</span>
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.sudokuarea.app"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-app-download-btn"
            aria-label={t.common.downloadGooglePlay}
            onClick={() => track('store_click', { store: 'android', placement: 'landing_download' })}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 1.6v20.8L13.8 12z" fill="#4285F4"/>
              <path d="M13.8 12L3 1.6l15.1 8.7z" fill="#EA4335"/>
              <path d="M13.8 12L3 22.4l15.1-8.7z" fill="#34A853"/>
              <path d="M18.1 10.3L13.8 12l4.3 1.7L21 12z" fill="#FBBC04"/>
            </svg>
            <span>{t.common.downloadGooglePlay}</span>
          </a>
        </div>
      </section>

      {/* FEATURES ─────────────────────────────────────────── */}
      <section className="landing-section">
        <header className="landing-section-head">
          <h2 className="landing-section-title">{tt.landingFeaturesTitle}</h2>
          <p className="landing-section-sub">{tt.landingFeaturesSub}</p>
        </header>
        <div className="landing-feature-grid">
          <article className="landing-feature-card landing-feature-card--green">
            <div className="landing-feature-icon"><IconBrain /></div>
            <h3>{tt.landingF1Title}</h3>
            <p>{tt.landingF1Desc}</p>
          </article>
          <article className="landing-feature-card landing-feature-card--amber">
            <div className="landing-feature-icon"><IconTrophy /></div>
            <h3>{tt.landingF2Title}</h3>
            <p>{tt.landingF2Desc}</p>
          </article>
          <article className="landing-feature-card landing-feature-card--rose">
            <div className="landing-feature-icon"><IconFlame /></div>
            <h3>{tt.landingF3Title}</h3>
            <p>{tt.landingF3Desc}</p>
          </article>
        </div>
      </section>

      {/* HOW IT WORKS ────────────────────────────────────── */}
      <section className="landing-section">
        <header className="landing-section-head">
          <h2 className="landing-section-title">{tt.landingHowTitle}</h2>
        </header>
        <ol className="landing-steps">
          <li>
            <span className="landing-step-num">1</span>
            <div>
              <h4>{tt.landingStep1}</h4>
              <p>{tt.landingStep1Desc}</p>
            </div>
          </li>
          <li>
            <span className="landing-step-num">2</span>
            <div>
              <h4>{tt.landingStep2}</h4>
              <p>{tt.landingStep2Desc}</p>
            </div>
          </li>
          <li>
            <span className="landing-step-num">3</span>
            <div>
              <h4>{tt.landingStep3}</h4>
              <p>{tt.landingStep3Desc}</p>
            </div>
          </li>
        </ol>
      </section>

      {/* FINAL CTA ───────────────────────────────────────── */}
      <section className="landing-final">
        <div className="landing-final-card">
          <h2>{tt.landingFinalTitle}</h2>
          <p>{tt.landingFinalSub}</p>
          <div className="landing-ctas">
            <button
              type="button"
              className="landing-cta landing-cta--primary"
              onClick={() => goToAuth('landing_final')}
            >
              {tt.landingRegisterCta}
            </button>
            <button
              type="button"
              className="landing-cta landing-cta--ghost"
              onClick={() => continueAsGuest('landing_final')}
            >
              {tt.landingGuestCta}
            </button>
          </div>
        </div>
      </section>

      <HomeSeoBlocks />
    </div>
  );
}
